/**
 * Tiny localStorage-backed favorites store for the provider model picker.
 *
 * Each favorite is a `${provider}:${slug}` key. Persisted as a JSON array so
 * the on-disk representation is human-readable. Falls back to no-op on
 * environments without localStorage (e.g. SSR/preview) instead of throwing.
 */

import { useCallback, useSyncExternalStore } from "react";

const STORAGE_KEY = "forge.modelPicker.favorites.v1";

type FavoritesSnapshot = ReadonlyArray<string>;

let snapshot: FavoritesSnapshot = readFromStorage();
const listeners = new Set<() => void>();

function readFromStorage(): FavoritesSnapshot {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((value): value is string => typeof value === "string");
  } catch {
    return [];
  }
}

function writeToStorage(next: FavoritesSnapshot): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    // Quota or storage disabled — degrade silently. The in-memory snapshot
    // still reflects the toggle for the current session.
  }
}

function notify(): void {
  for (const listener of listeners) listener();
}

export function favoriteKey(provider: string, slug: string): string {
  return `${provider}:${slug}`;
}

/**
 * Which `${provider}:${slug}` keys in a model list are favorited. Lives here
 * rather than inline in the picker component so ModelPickerContent stays
 * within its file-size ratchet.
 */
export function computeFavoriteModelKeys(
  modelOptionsByProvider: Record<string, ReadonlyArray<{ readonly slug: string }> | undefined>,
  isFavorite: (provider: string, slug: string) => boolean,
): ReadonlySet<string> {
  const set = new Set<string>();
  for (const provider of Object.keys(modelOptionsByProvider)) {
    for (const option of modelOptionsByProvider[provider] ?? []) {
      if (isFavorite(provider, option.slug)) set.add(favoriteKey(provider, option.slug));
    }
  }
  return set;
}

export function getFavoritesSnapshot(): FavoritesSnapshot {
  return snapshot;
}

export function toggleFavoriteModel(provider: string, slug: string): void {
  const key = favoriteKey(provider, slug);
  const exists = snapshot.includes(key);
  snapshot = exists ? snapshot.filter((entry) => entry !== key) : [...snapshot, key];
  writeToStorage(snapshot);
  notify();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function useFavoriteModels(): {
  favorites: ReadonlySet<string>;
  isFavorite: (provider: string, slug: string) => boolean;
  toggle: (provider: string, slug: string) => void;
} {
  const current = useSyncExternalStore(subscribe, getFavoritesSnapshot, getFavoritesSnapshot);
  const set = useFavoritesSet(current);
  const toggle = useCallback((provider: string, slug: string) => {
    toggleFavoriteModel(provider, slug);
  }, []);
  const isFavorite = useCallback(
    (provider: string, slug: string) => set.has(favoriteKey(provider, slug)),
    [set],
  );
  return { favorites: set, isFavorite, toggle };
}

// Memoize the Set construction across renders that don't change the array
// identity. useSyncExternalStore guarantees referential stability when the
// snapshot is unchanged, so we can cache one Set per snapshot.
const setCache = new WeakMap<FavoritesSnapshot, ReadonlySet<string>>();
function useFavoritesSet(current: FavoritesSnapshot): ReadonlySet<string> {
  let cached = setCache.get(current);
  if (!cached) {
    cached = new Set(current);
    setCache.set(current, cached);
  }
  return cached;
}
